/**
 * A new contact thumbnail component that will replace the legacy contact-thumbnail component as we continue to refactor recipients and contact UI.
 *
 * Usage:
 * The goal of refactoring this was primarily to make the component more useful without requiring classNames (prev lots of dupe)
 * - by default it uses the prop preset='main' for styling, so in most cases you shouldn't have to pass any styling to the component
 * - you can pass positioning related styling (margin: '0 auto', etc.) as className if you need to (avoid changing width/height with className)
 * - if you need a diameter other than the presets, you can change width you can pass diameter=30 (e.g.) and fonts etc. will adjust proportionally
 *
 * Other Features:
 * - PayPal gradients behind initials
 * - Updated silhouette
 * - Fades the profile photo on load, instead of gross scanning
 */

import { connect } from 'transfer/utils/p2p-connect'
import PropTypes from 'prop-types'
import React from 'react'
import content from 'pp-react-l10n'
import silhouetteSVG from '../../../../lib/inline-icons/silhouette.svg'
import glamorousIcon from '../../lib/glamorous-icon'
import glamorous from 'glamorous'
let i18n = content('transfer/page/preview')

const SilhouetteIcon = glamorousIcon()

const gradients = [
  'linear-gradient(-25deg, #00CF92, #009CDE)',
  'linear-gradient(-25deg, #00CF92, #009CDE)',

  'linear-gradient(100deg, #FF9600, #DE0063)',
  'linear-gradient(100deg, #FF9600, #DE0063)',

  'linear-gradient(10deg, #009CDE, #003087)',
  'linear-gradient(10deg, #009CDE, #003087)',

  'linear-gradient(100deg, #640487, #003087)',
  'linear-gradient(100deg, #640487, #003087)',

  'linear-gradient(10deg, #DE0063, #640487)',
  'linear-gradient(10deg, #DE0063, #640487)',
]

const sizes = {
  main: {
    diameter: 96,
  },
  modern: {
    diameter: 79,
  },
  mini: {
    diameter: 50,
  },
}

/**
 * Function used to deterministically produce the same index from a first initial.
 * Used so background gradients are the same between component renders, and user sessions.
 * The math could probably be more eloquent...
 */
export function generateGradientFromFirstInitial(firstInitial) {
  if (!firstInitial) {
    return gradients[0]
  }
  // e.g. charCode = 27; 27 % 10 = 7 i.e. remainder will ALWAYS be between 0 and 9
  const remainder = firstInitial.charCodeAt(0) % 10
  return gradients[remainder] || gradients[0]
}

/**
 * PayPal services always return some string for profile photos, even if it's a url to a blank image.
 * This is helper to determine if there is actually a photo to show.
 */
export function hasProfilePhoto(thumbnailUrl) {
  return !!thumbnailUrl && !thumbnailUrl.includes('&d=blank')
}

/**
 * If the contact doesn't already have a thumbnail or initials, render a generic user silhouette thumbnail
 * NOTE: this was copied from an older component — we should replace with newer sillhouette soon.
 * */
function Silhouette({ diameter: diameterProp, preset }) {
  const diameter = diameterProp || sizes[preset].diameter
  const size = diameter * 0.55 // size of silhouette looks good when it's 55% of diameter
  return (
    <glamorous.Div
      width={size}
      height={size}
      margin="50% auto 0 auto"
      transform="translateY(-50%)"
    >
      <SilhouetteIcon svg={silhouetteSVG} />
    </glamorous.Div>
  )
}
Silhouette.propTypes = {
  diameter: PropTypes.number,
  preset: PropTypes.string,
}

/**
 * Includes initials for names that support it, with a gradient background
 */
function Initials({
  firstInitial,
  lastInitial,
  preset,
  diameter: diameterProp,
}) {
  const diameter = diameterProp || sizes[preset].diameter
  const fontSize = diameter * 0.3 // font looks good when it's 30% of the diameter
  return (
    <glamorous.P
      color="white"
      position="relative"
      margin="0"
      top="50%"
      transform="translateY(-50%)"
      fontSize={fontSize}
    >
      {i18n('recipient.initials', {
        firstInitial,
        lastInitial,
      })}
    </glamorous.P>
  )
}
Initials.propTypes = {
  firstInitial: PropTypes.string,
  lastInitial: PropTypes.string,
  preset: PropTypes.string,
  diameter: PropTypes.number,
}

export function ProfileImage(props) {
  return (
    <glamorous.Div
      height="100%"
      width="100%"
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundImage={`url('${props.thumbnailUrl}')`}
    />
  )
}
ProfileImage.propTypes = {
  thumbnailUrl: PropTypes.string,
}

const Wrapper = glamorous.div(
  {
    position: 'relative',
    borderRadius: '50%',
    overflow: 'hidden',
    boxShadow: '1px 3px 8px rgba(199, 199, 199, 0.50)',
    textAlign: 'center',
    backgroundSize: 'cover',
  },
  ({ diameter, preset, showGradient, firstInitial }) => ({
    width: diameter || sizes[preset].diameter,
    height: diameter || sizes[preset].diameter,
    backgroundImage: showGradient
      ? generateGradientFromFirstInitial(firstInitial)
      : '',
  })
)

export function ContactThumbnail({
  className,
  thumbnailUrl = '',
  preset,
  diameter,
  firstInitial,
  lastInitial,
  permissions = {},
}) {
  const showProfileImage = hasProfilePhoto(thumbnailUrl)
  const showGradient = !showProfileImage
  const showInitials =
    !showProfileImage &&
    permissions.canUseInitials &&
    firstInitial &&
    lastInitial
  const showSilhouette = !showProfileImage && !showInitials
  return (
    <Wrapper
      showGradient={showGradient}
      firstInitial={firstInitial}
      preset={preset}
      diameter={diameter}
      className={className}
    >
      {showProfileImage && <ProfileImage thumbnailUrl={thumbnailUrl} />}
      {showInitials && (
        <Initials
          firstInitial={firstInitial}
          lastInitial={lastInitial}
          preset={preset}
          diameter={diameter}
        />
      )}
      {showSilhouette && <Silhouette diameter={diameter} preset={preset} />}
    </Wrapper>
  )
}

ContactThumbnail.propTypes = {
  preset: PropTypes.oneOf(['main', 'modern', 'mini']), // main is normal, modern is simple header, mini is like autocomplete sized
  diameter: PropTypes.number,
  className: PropTypes.string,
  // :) resist adding other className overrides for things like font color, background color, in general all contact thumbnails should look/work the same
  thumbnailUrl: PropTypes.string.isRequired,
  firstInitial: PropTypes.string,
  lastInitial: PropTypes.string,
  permissions: PropTypes.object.isRequired,
}

ContactThumbnail.defaultProps = {
  className: '',
  thumbnailUrl: '',
  permissions: {},
  preset: 'main',
}

export default connect(({ server }) => ({
  permissions: server.permissions,
}))(ContactThumbnail)
